// Billing/plan API reads + shared request config for the tenant billing surface
// (issue 213).
//
// The plan catalog + lifecycle live on system-admin-api (masterApi), which does
// NOT auto-attach X-Tenant-Slug — callers name the tenant per request (the same
// per-call header pattern as aiUsage.ts). The card/profile/handoff routes live
// on payment-api (paymentApi), which DOES auto-attach the slug. Centralising the
// header wiring here keeps every billing caller consistent and testable, and
// keeps utils/api.ts (owned by the banner work) untouched.
//
// This module holds the READS plus the shared per-call header config. The billing
// WRITES do not live here (issue 195, D5): a plan change, a seat booking, a
// cancellation or a card removal is a user-initiated write, and the seam that owns
// its pending state and reports its failure next to the button that was pressed can
// only be opened at the call site. Each of those sites issues its own POST/PUT/DELETE
// inside `useApiMutation`, using `masterCfg()` from here so the header wiring stays
// in one place. What this module still shares is meaning, not transport.
import { masterApi, userApi } from './api';
import { currentTenantSlug } from './runtime-config';

export interface PlanOption {
  planKey: string;
  displayKey: string;
  // Per-seat recut (2026-08): self-serve plans price per seat ('users' or
  // 'apprentices'); the fixed monthly price survives only on legacy shapes.
  pricePerSeatCents?: number | null;
  seatMetric?: 'users' | 'apprentices' | null;
  priceMonthlyCents: number | null;
}
export interface ModuleCatalog {
  options: PlanOption[];
  hasEnterprise: boolean;
}
export interface CatalogModule {
  moduleKey: string;
  currentPlan: string;
  status: string;
  catalog: ModuleCatalog;
}
// A module the tenant does NOT have and may buy (issue 213 G13). The backend
// derives this as the exact inverse of add-module's MODULE_ALREADY_ACTIVE gate
// (one shared `isModulePresent` predicate), so anything listed here is something
// /plan/add-module accepts — the picker can never offer a module the POST 409s.
// The core modules every workspace has (`people`, `payroll`) carry no priced
// plan and are never listed.
export interface AddableModule {
  moduleKey: string;
  catalog: ModuleCatalog;
}
export interface PlanLine {
  moduleKey: string;
  planKey: string;
  displayKey: string;
  pricePerSeatCents?: number | null;
  seatCount?: number | null;
  amountCents?: number | null;
  priceMonthlyCents?: number | null;
  // The promo line of a frozen invoice carries the code and percentage it was
  // priced with (ADR 0100 D6), so its label can name them.
  promoCode?: string | null;
  promoPercent?: number | null;
  // The referral line the euro cap lowered (`discount.referral`): its label says so.
  promoCapped?: boolean;
  // A module code's line (`discount.module`, issue 1029) names the catalog module it discounts.
  promoModuleKey?: string | null;
}
// An open scheduled change. FOUR slots can be open at once (issue 299 D25): a
// module resize, a module removal, the account-wide cancellation, and one seat
// change PER METRIC — so a reader must match on `changeType`/`seatMetric` and can
// never assume the first entry is the one it wants.
export interface ScheduledChange {
  id: string;
  moduleKey: string | null;
  changeType: string;
  targetPlan: string | null;
  effectiveAt: string;
  // Set only on a 'seat_change' (moduleKey is null there): which booking moves and
  // to what. Without them a pending reduction reads as an anonymous date the
  // workspace can neither judge nor decide to revert.
  // 'ai_quota_steps' is the AI quota upgrade decrease (#665): the same slot
  // shape, booked steps instead of seats — so a seat reader must match on its
  // own metric and never on `changeType` alone.
  seatMetric?: 'users' | 'apprentices' | typeof AI_QUOTA_STEPS_METRIC | null;
  targetSeats?: number | null;
}
export interface Standing {
  inTrouble: boolean;
  cause: string | null;
  blocksUpgrade: boolean;
  // Cause refinement for the app-wide billing banner (issue 213 F13b/F36): the
  // payment decline sub-cause and the concrete seat overage, so the bar can be as
  // specific as the day-0 nudge. Optional — the client-side fallback standing (before
  // the catalog loads) legitimately omits them.
  subCause?: string | null;
  breachModule?: string | null;
  breachObserved?: number | null;
  breachIncluded?: number | null;
}
// Booked seats (ADR 0056): what the tenant pays on — bookedUserSeats (1 =
// freelancer mode, or ≥ minCompanyUserSeats in steps of userSeatStep) and
// bookedApprenticeSeats (per head) — plus the tier rules so the stepper cannot
// offer an unbookable number (from freelancer mode the next bookable value is
// minCompanyUserSeats, not 1 + step; above selfServeMaxUserSeats there is no
// self-serve tier at all and the POST refuses with SEAT_ENTERPRISE_CONTACT).
export interface BookedSeats {
  userSeats: number;
  apprenticeSeats: number;
  minCompanyUserSeats: number;
  userSeatStep: number;
  selfServeMaxUserSeats: number;
  // The apprentice metric's self-serve ceiling (#299 X4a): per-head, no ladder,
  // so unlike selfServeMaxUserSeats there is no minimum/step to go with it.
  // Above it the plan is a sales agreement (409 SEAT_ENTERPRISE_CONTACT) at both
  // add-module and the seat-change door — this UI reads the number over the
  // wire rather than keeping a second copy of it.
  selfServeMaxApprenticeSeats: number;
}
// What actually occupies the booking right now (issue 299 D21) — the SAME counts
// the invite/creation gates refuse on and the decrease floor is checked against,
// so the panel and the wall can never quote different numbers. `apprentices` is
// null when the live count could not be established: the panel then omits the
// figure rather than showing an unverified zero.
export interface SeatUsage {
  users: number;
  apprentices: number | null;
}
// A scheduled seat DECREASE that applied at a HIGHER quantity than it asked for
// (issue 299, audit F1). The floor runs when the reduction is requested; people
// invited between then and the billing date are checked against the OLD booking, so
// the population can legally outgrow the number that was about to be written. The
// apply then clamps to the smallest bookable quantity that still holds them — and
// this is how the workspace is told, since nothing else would explain why it asked
// for 10 seats and is being billed for 45.
//
// Derived server-side from the applied change row plus the current booking; it is
// reported only while it is still TRUE (the booking is exactly the floor the live
// population forces), so it disappears by itself once people are removed.
export interface SeatDecreaseAdjustment {
  seatMetric: 'users' | 'apprentices';
  // What the workspace asked for.
  requestedSeats: number;
  // What it is booked — and billed — at instead.
  bookedSeats: number;
  // The live population that forced it.
  usedSeats: number;
  appliedAt: string | null;
}
// What the booking BUYS and what it costs a month (issue 364 F30) — the inputs the
// in-product price calculator runs on.
//
// Every number here is system-admin-api's, off `plans/plan-limits.ts`, which
// `scripts/check-pricing-catalog.mjs` proves equal to the repo-root
// `pricing.catalog.json`. The workspace UI therefore states prices without holding
// a single one: ADR 0001 forbids importing the constant across the service
// boundary, and a hand-copied ladder here would be exactly the second copy that
// guard exists to prevent — so the price list travels as payload instead, like
// `selfServeMaxApprenticeSeats` already does.
//
// The UI does the ARITHMETIC (utils/seatPricing.ts) so the total follows the
// stepper without a request per click, and it proves that arithmetic against
// `monthlyTotalCents` — the billed sum at TODAY's booking — before it projects any
// other booking.
export interface SeatPricingLine {
  moduleKey: string;
  displayKey: string;
  pricePerSeatCents: number;
  seatMetric: 'users' | 'apprentices';
}
export interface SeatVolumeBand {
  // 1-based index of the first seat in the band.
  fromSeat: number;
  // 1-based index of the last seat, or null for the open-ended top band.
  toSeat: number | null;
  // Percent off the per-seat price for the seats INSIDE this band only.
  discountPct: number;
}
export interface SeatPricing {
  // The priced lines the booking buys — the "what do I get" half of the question.
  lines: SeatPricingLine[];
  // The summed per-seat price of each metric's lines: the ramp's input, summed
  // before the ramp because a seat is one seat across the whole subscription.
  pricePerSeatCents: { users: number; apprentices: number };
  volumeBands: SeatVolumeBand[];
  // Which booked metrics the bands ramp. A metric absent from this list bills at
  // list price — a pricing fact, not a display one, so it is declared and not
  // assumed.
  bandedMetrics: ('users' | 'apprentices')[];
  // The billed monthly total at the CURRENT booking, as the invoice sums it.
  monthlyTotalCents: number;
}
// THE AI QUOTA (#665): how much Lumi the workspace may spend per BILLING period —
// what the booked seats include (or the operator's figure for that half) plus the
// booked upgrade steps. `limit` null = no ceiling at all (the workspace's own
// provider key, or an operator's explicit unlimited). Every figure is the
// server's, off the same resolver enforcement uses, so the card and the wall
// cannot quote different numbers; this UI derives none of them.
export type AiQuotaSource = 'derived' | 'override' | 'unlimited' | 'own_key' | 'suspended';
export interface AiQuota {
  limit: number | null;
  source: AiQuotaSource;
  // The included half: seat-derived, or the operator override that replaces it.
  included: number | null;
  // What the seats WOULD include, whichever source is in force.
  derived: number;
  // Tokens the booked upgrade steps add, and the steps themselves.
  upgrades: number;
  upgradeSteps: number;
  // Spend in the current quota period.
  used: number;
  periodStart: string;
  // The reset: the next billing date (a trial runs monthly from provisioning).
  periodEnd: string;
  period: string;
  periodSource: 'billing' | 'trial';
}
// The Billing card's extra facts (/plan/catalog `aiQuota`): what one step adds and
// costs, the self-serve ceiling, and the booking as it stands.
export interface AiQuotaCard extends AiQuota {
  ownKey: boolean;
  bookedUpgrades: number;
  stepTokens: number;
  stepPriceCents: number;
  selfServeMaxUpgrades: number;
}
// The billed line and the scheduled-change metric the upgrade rides on — the
// server's names, spelled once here so every reader matches the same strings.
export const AI_QUOTA_MODULE_KEY = 'ai_quota';
export const AI_QUOTA_STEPS_METRIC = 'ai_quota_steps';

export interface PlanCatalog {
  modules: CatalogModule[];
  addableModules: AddableModule[];
  currentLines: PlanLine[];
  bookedSeats: BookedSeats;
  // Optional: an older API (or a test double) that does not send it leaves the
  // calculator unrendered rather than guessing a price.
  seatPricing?: SeatPricing | null;
  seatUsage: SeatUsage;
  scheduledChanges: ScheduledChange[];
  seatDecreaseAdjustments: SeatDecreaseAdjustment[];
  // The billing account as payment-api's summary reports it, passed through by
  // the catalog. `trialStartedAt` / `nextChargeAt` are what the demo exit gate
  // (#409 M3) states next to the booking; optional because an older API or a
  // test double may not send them, in which case the gate omits the line rather
  // than guessing a date.
  account: ({ status: string; trialStartedAt?: string | null; trialEndsAt: string | null; nextChargeAt?: string | null } & PromoFields) | null;
  // cardLabel is Mollie's brand only ("Mastercard"); cardNumberSuffix holds the
  // last four digits; cardExpiryDate is Mollie's "YYYY-MM".
  paymentMethod: { status: string; cardLabel: string | null; cardNumberSuffix?: string | null; cardExpiryDate: string | null } | null;
  cancellationRequestedAt: string | null;
  standing: Standing;
  // The AI quota card (#665). Optional: an older API, or a test double, that does
  // not send it renders no card rather than a guessed quota.
  aiQuota?: AiQuotaCard | null;
  // Payments enforcement stage (issue 213): stage decides whether the lockout bar
  // shows at all ('warning' — the ladder's only pre-lockout stage since the
  // read-only one was deleted), standing.cause the cause-aware manager CTA.
  enforcementStage?: string | null;
}
export interface ChangeResult {
  applied: 'immediate' | 'scheduled' | 'noop';
  charged?: boolean;
  amountCents?: number;
  effectiveAt?: string;
  scheduledChangeId?: string;
}
// Present only when a module re-add's REQUESTED apprentice booking was floored
// up to what the workspace was already booked (and billed) for before the
// module was removed (issue 299, X3's re-add floor). Same shape as
// SeatDecreaseAdjustment's requested/booked pair — the identical situation in
// the other direction — carried only while it is true, never on an
// unadjusted add.
export interface ApprenticeSeatAdjustment {
  requestedSeats: number;
  bookedSeats: number;
}
export interface AddModuleResult {
  // Describes the entitlement + the billing, both of which are immediate for every
  // addable module — NOT the provisioning (see `status`).
  applied: 'immediate';
  moduleKey: string;
  plan: string;
  // The module row's REAL status. Only `ats` is synchronous and comes back
  // 'active'; time_tracking / apprenticeship are worker-driven and come back
  // 'provisioning' on their way to active|failed (issue 213 G13).
  status: string;
  charged: boolean;
  amountCents: number;
  apprenticeSeatAdjustment?: ApprenticeSeatAdjustment | null;
  // Only when the add carried a module code (issue 1029, M2): what landed. `null` =
  // the module was added but the code did not land - the confirmation says so.
  promo?: { code: string; percent: number; months: number } | null;
  // `promo: null` + `promoPending`: the answer was indeterminate - the code is held and reconciled, so
  // it will show on the next bill once confirmed (not "could not be applied").
  promoPending?: boolean;
}
// What /plan/change or /plan/add-module would take from the card today (issue 213
// G12), computed by the same quote the charge itself uses. `amountCents` is the
// gross; 0 is a legitimate "no charge today", not an error.
export interface ProrationPreview {
  amountCents: number;
  netCents: number;
  vatCents: number;
  taxMode: string | null;
  periodEnd: string | null;
  // Seat DECREASE only (issue 299 D9): a reduction is never priced — it takes
  // effect at the next billing date, and `effectiveAt` is the date the confirm
  // has to name. `scheduled: false` is the trialing case, which applies at once
  // (D22) and has no date to name.
  scheduled?: boolean;
  effectiveAt?: string | null;
  // The same re-add-floor notice as AddModuleResult, quoted here too so the
  // confirm modal can say it BEFORE the card is debited, not only after.
  apprenticeSeatAdjustment?: ApprenticeSeatAdjustment | null;
  // An ADD quoted with a module code (issue 1029, M2): the verdict on the code, and
  // - when it is valid - what it takes off the amount above (already netted out of
  // `amountCents`, so the figure the dialog names is the figure the charge takes).
  promo?: ModulePromoVerdict;
  discountCents?: number;
}
export interface LifecycleState {
  tenant: { id: string; slug: string; name: string; status: string; purgedAt: string | null };
  openRequest: { id: string; status: string; reason: string | null; requestedAt: string; graceUntil: string | null } | null;
}

// The seat gates refuse with more than a code: the floor refusal carries the metric,
// what is actually in use and the smallest booking that still holds it. Read it off
// the refusal rather than recomputing a floor client-side — the server did the
// counting, and a second opinion here is how the two answers drift apart. The seam
// hands the thrown error to `onError` as `outcome.error`, which is what this reads;
// the plain `code`/`status` the old helpers dug out come off the outcome itself.
export function seatRefusal(
  e: unknown,
): { seatMetric?: 'users' | 'apprentices'; usedSeats?: number; minimumBookableSeats?: number } | undefined {
  const data = (e as { response?: { data?: { code?: string; seatMetric?: 'users' | 'apprentices'; usedSeats?: number; minimumBookableSeats?: number } } })
    ?.response?.data;
  if (!data?.code) return undefined;
  return { seatMetric: data.seatMetric, usedSeats: data.usedSeats, minimumBookableSeats: data.minimumBookableSeats };
}

// The ONE description of what a catalog option costs (issue 261 M2). It was the
// billing plan section's private pair of helpers until the workspace home's module
// teasers began quoting a price too — and two copies of a price sentence is
// precisely the drift PDR 0017 §5 exists to prevent, one level down from the
// constants it guards. Both surfaces now say the same thing about the same number.
//
// The price and the volume note are returned TOGETHER on purpose. They are one
// claim, not two: the figure is the CATALOG rate, which is pre-ramp, and real
// invoices are marginal-volume discounted by booked seats (ADR 0056). Splitting
// them let one surface show the rate while the other decided separately whether to
// qualify it — which is how a legacy fixed-price option ends up carrying a
// "discounted by booked seats" promise that does not apply to it.
//
// A module's display name off the billing catalogue, the key itself when the
// catalogue has none. One helper for every surface that names a module next to
// its price (the plan section, the demo exit gate that hosts it, #409 F12).
export function moduleLabel(moduleKey: string, t: (key: string) => string): string {
  const key = `billing.moduleNames.${moduleKey}`;
  const label = t(key);
  return label === key ? moduleKey : label;
}

// ── The promo (issue 808, ADR 0100) ─────────────────────────────────────────
// A promo code is a percentage for a number of months. At 100 % (`free_months`)
// the trial is followed by cardless free months: `freeFrom` is the trial's own
// end, `freeUntil` is `trialEndsAt` — the day the first charge can fall, so the
// "charges from" date needs no promo arithmetic here. Below 100 % (`discount`)
// the window opens at conversion and both dates are null until then. What the
// account block says about it, on /billing/overview and on the catalog's
// `account` alike. Optional: an older API, or a test double, that does not send
// it reads as "no promo" rather than a guessed one.
export type AccountPromo = {
  code: string;
  percent: number;
  months: number;
  kind: 'free_months' | 'discount';
  freeFrom: string | null;
  freeUntil: string | null;
  // `discount` only: the window opens at conversion (null until then); `discountUntil`
  // is exclusive, the same way an invoice's `periodEnd` is.
  discountFrom?: string | null;
  discountUntil?: string | null;
};
// A referral bonus this workspace earned (issue 1029): what it gave, the invoices
// that carried a month of it and how many months are left. Payment-api holds only
// master's opaque redemption id - never the friend behind the bonus.
// A new bonus waits READY until a billing person collects it (issue 1081), then
// RUNS for its months and is DONE when they are used. `collect` rides ready ones
// only: the server's verdict on pressing Collect now (the planner's own projection),
// with the reason when it is off; `ceilingPercent` is the most one bill carries.
export type CollectRefusalReason = 'ceiling' | 'workspace_not_active' | 'deletion_pending' | 'unavailable';
export type ReferralBonus = {
  redemptionId: string;
  earnedAt: string;
  percent: number;
  months: number;
  state?: 'ready' | 'running' | 'done';
  collectedAt?: string | null;
  // The euro cap of one invoice's reduction, frozen at earn time; null = earned before the cap (uncapped).
  capCents?: number | null;
  // `running` only, and only where the overview projects the next invoice: whether the planner puts
  // this bonus on it. Absent when master's basket could not be read.
  onNextInvoice?: boolean;
  collect?: { allowed: boolean; reason: CollectRefusalReason | null; ceilingPercent: number | null };
  used: { periodStart: string; invoiceId: string }[];
  remaining: number;
};
// The discounts the next monthly invoice will carry, as payment-api's biller will
// freeze them (issue 1029): the checkout grant's line, a module code's line per
// module (`percent` = the effective one, trimmed under the ceiling where it is),
// and ONE summed referral line, each with its amount in cents (negative, as on
// the line).
export type NextDiscount =
  | { kind: 'promo'; percent: number; code: string; amountCents: number }
  | { kind: 'module'; percent: number; code: string; moduleKey: string; amountCents: number }
  | { kind: 'referral'; percent: number; amountCents: number; capped: boolean };
// A module code this workspace redeemed when it added a module (issue 1029, M2): the
// percent off that module's lines for `months` issued invoices, the invoices that
// carried a month of it and how many are left. The add's own proration is extra and
// uses no month.
export type ModuleGrant = {
  redemptionId: string;
  code: string;
  moduleKey: string;
  percent: number;
  months: number;
  grantedAt: string;
  used: { periodStart: string; invoiceId: string }[];
  remaining: number;
};
export type PromoFields = {
  promo?: AccountPromo | null;
  referralBonuses?: ReferralBonus[];
  moduleGrants?: ModuleGrant[];
  // Null when payment-api could not read the basket: then nothing is projected.
  nextDiscounts?: NextDiscount[] | null;
};

// The verdict on a module code typed in the add dialog (POST /plan/module-promo/validate,
// and the `promo` of the proration preview): the shared promo vocabulary plus
// `wrong_checkout` for a code that belongs to the new-sign-up checkout.
export type ModulePromoVerdict =
  // `effectivePercent` / `trimmed` ride on the PREVIEW's verdict only: the percent the module line carries
  // today beside the sign-up code (0 = trimmed to nothing), and whether the ceiling cut the code's own.
  | { valid: true; code: string; label?: string; percent: number; months: number; effectivePercent?: number; trimmed?: boolean }
  | { valid: false; reason: string };

// The check itself is a POST (`/plan/module-promo/validate`, read-only), issued at its call
// site in the add dialog inside the mutation seam like every POST of this UI.

// One plain sentence for a refused module code. Every reason the server can answer,
// each with its own literal key (the unused-key scan reads literals); anything it
// does not name falls to the general "cannot be used here".
export function modulePromoReasonMessage(
  reason: string | undefined,
  t: (key: string, named?: Record<string, unknown>) => string,
  module: string,
): string {
  switch (reason) {
    case 'unknown': return t('billing.modulePromo.reason.unknown');
    case 'disabled': return t('billing.modulePromo.reason.disabled');
    case 'not_yet_valid': return t('billing.modulePromo.reason.notYetValid');
    case 'expired': return t('billing.modulePromo.reason.expired');
    case 'exhausted': return t('billing.modulePromo.reason.exhausted');
    case 'module_not_eligible': return t('billing.modulePromo.reason.moduleNotEligible', { module });
    case 'wrong_checkout': return t('billing.modulePromo.reason.wrongCheckout');
    default: return t('billing.modulePromo.reason.unavailable');
  }
}

// The one promo sentence the billing page and the demo exit gate both say, for
// the phase the account is in — or null when there is nothing true to say:
//  - before the run (`now` < freeFrom): "Trial until d1 · then 3 free months until d2 · code X"
//  - inside the run: "3 free months until d2 · code X"
//  - after the run, or once the account has left the trial: nothing
//  - a discount, while still trialing: "Trial until d1 · then N months at P % off seats and modules · code X"
//  - a discount, converted and inside its window: "P % off seats and modules until d2 · code X"
//  - a discount after its window, or with none: nothing
// d2 is `discountUntil` as it stands, the way the page shows an invoice's period
// end and the free run's own end: the date the window closes, not a day earlier.
// Dates go through the caller's formatter, so each surface keeps its own date
// style; `now` is the caller's clock.
// Whether the NEXT invoice of a paying account sits inside its discount window.
// payment-api's `withPromoLine` decides by the invoiced period - `from <=
// periodStart < until` - so this does too, with the page's own "charges from"
// date (`nextChargeAt`, the next period's start) as the period start. `now` is
// only the fallback for a summary that carries no such date. An active account
// and a delinquent one (its debt and recovery charge are still discounted) both
// count; a canceled or suspended one has no next charge to discount.
function nextInvoiceInWindow(
  account: { status?: string | null; nextChargeAt?: string | null },
  promo: AccountPromo,
  now: Date,
): boolean {
  if (account.status !== 'active' && account.status !== 'delinquent') return false;
  if (!promo.discountUntil) return false;
  const until = new Date(promo.discountUntil);
  const at = account.nextChargeAt ? new Date(account.nextChargeAt) : now;
  if (Number.isNaN(until.getTime()) || Number.isNaN(at.getTime())) return false;
  if (promo.discountFrom) {
    const from = new Date(promo.discountFrom);
    if (!Number.isNaN(from.getTime()) && at < from) return false;
  }
  return at < until;
}

// Whether the account's promo code is still in play - the phase in which a buyer
// can still be misled about what it covers. A free run: while the account is
// trialing (the run IS the trial). A discount: while it is promised (trialing)
// or while the next invoice's period starts inside its window. A closed window and
// a converted free run are over, so the overview's remaining `promo` block names
// nothing that applies any more.
export function promoRunning(
  account: ({ status?: string | null; nextChargeAt?: string | null } & PromoFields) | null | undefined,
  now: Date,
): boolean {
  const promo = account?.promo;
  if (!account || !promo) return false;
  const trialing = !account.status || account.status === 'trialing';
  if (promo.kind === 'free_months') {
    if (!trialing) return false;
    const until = promo.freeUntil ? new Date(promo.freeUntil) : null;
    return !until || Number.isNaN(until.getTime()) || now < until;
  }
  return trialing || nextInvoiceInWindow(account, promo, now);
}

export function describePromo(
  a: ({ status?: string | null; trialEndsAt?: string | null; nextChargeAt?: string | null } & PromoFields) | null | undefined,
  now: Date,
  i18n: { t: (key: string, named?: Record<string, unknown>, plural?: number) => string; date: (iso: string) => string },
): string | null {
  const promo = a?.promo;
  if (!promo) return null;
  const { code, months: n } = promo;
  const trialing = !a?.status || a.status === 'trialing';
  if (promo.kind === 'discount') {
    const percent = promo.percent;
    if (promo.discountUntil) {
      // Running only while the next invoice is still inside the window (see nextInvoiceInWindow).
      if (!a || !nextInvoiceInWindow(a, promo, now)) return null;
      return i18n.t('billing.promo.discountRun', { percent, end: i18n.date(promo.discountUntil), code });
    }
    // No window yet: it opens at conversion, so only a trialing account has a promise to make.
    if (!trialing || !a?.trialEndsAt) return null;
    return i18n.t('billing.promo.discount', { start: i18n.date(a.trialEndsAt), percent, n, code }, n);
  }
  if (!trialing) return null;
  if (!promo.freeFrom || !promo.freeUntil) return null;
  const from = new Date(promo.freeFrom);
  const until = new Date(promo.freeUntil);
  if (Number.isNaN(from.getTime()) || Number.isNaN(until.getTime()) || now >= until) return null;
  const end = i18n.date(promo.freeUntil);
  if (now < from) return i18n.t('billing.promo.trial', { start: i18n.date(promo.freeFrom), end, n, code }, n);
  // Months LEFT are not known here (only the total), so the run line names no count.
  return i18n.t('billing.promo.run', { end, code });
}

// The discount lines the next invoice will carry, for display: payment-api's
// `nextDiscounts` turned into lines, AMOUNTS AS GIVEN. The server computes them
// with the very function the biller freezes with, on master's basket for that
// period (a trialing account included: its first paid period, where the
// checkout window opens), so nothing is re-derived here and the page cannot
// promise what the invoice will not carry (issue 1029). No `nextDiscounts`
// (an older API, or a basket payment-api could not read) projects nothing.
export function projectedDiscountLines(account: PromoFields | null | undefined): PlanLine[] {
  return (account?.nextDiscounts ?? []).map((d): PlanLine => {
    if (d.kind === 'promo') {
      return { moduleKey: 'discount', planKey: 'promo', displayKey: 'discount.promo', amountCents: d.amountCents, promoCode: d.code, promoPercent: d.percent };
    }
    if (d.kind === 'module') {
      return {
        moduleKey: 'discount', planKey: 'module', displayKey: 'discount.module', amountCents: d.amountCents,
        promoCode: d.code, promoPercent: d.percent, promoModuleKey: d.moduleKey,
      };
    }
    return { moduleKey: 'discount', planKey: 'referral', displayKey: 'discount.referral', amountCents: d.amountCents, promoPercent: d.percent, ...(d.capped ? { promoCapped: true } : {}) };
  });
}

/** The billed lines as the next invoice will carry them: `lines` plus the projected discount lines, if any. */
export function withProjectedPromo(
  lines: PlanLine[] | null | undefined,
  account: ({ status?: string | null } & PromoFields) | null | undefined,
): PlanLine[] {
  const discounts = projectedDiscountLines(account);
  return discounts.length ? [...(lines ?? []), ...discounts] : (lines ?? []);
}

// The monthly sum of the billed lines — and only when every line carries an
// amount, so a total can never quietly omit a line it stands for. The section's
// sum row and the gate's "€X per month is charged" sentence both read this, so
// one screen shows one figure from one source (the server prices the lines;
// `seatPricing.monthlyTotalCents` is the same sum, computed server-side).
export function sumBookingCents(lines: PlanLine[] | null | undefined): number | null {
  if (!lines?.length || !lines.every((l) => typeof l.amountCents === 'number')) return null;
  return lines.reduce((sum, l) => sum + (l.amountCents as number), 0);
}

// Takes the composer's `t`/`locale` rather than reaching for one: this is a util,
// and vue-i18n's is only resolvable inside a component.
export function describeOptionPrice(
  o: PlanOption,
  i18n: { t: (key: string, named?: Record<string, unknown>) => string; locale: string },
): { price: string; volumeHint: string | null } {
  const euro = (cents: number) =>
    new Intl.NumberFormat(i18n.locale, { style: 'currency', currency: 'EUR' }).format((cents ?? 0) / 100);
  // Seat plans (the per-seat recut) price per user or per apprentice, and they are
  // the ones the ramp touches. A legacy fixed-price shape keeps the old flat "per
  // month" line and gets NO volume note — it is untouched by the ramp.
  if (o.pricePerSeatCents != null) {
    const amount = euro(o.pricePerSeatCents);
    return {
      price:
        o.seatMetric === 'apprentices'
          ? i18n.t('billing.plan.perApprenticeMonth', { amount })
          : i18n.t('billing.plan.perUserMonth', { amount }),
      // Numberless by design (issue 299): the band table is on no payload this UI
      // receives, so the hint states the qualitative fact and never a threshold or
      // percentage that would be an unguarded second copy of numbers
      // `scripts/check-pricing-catalog.mjs` proves only for landing-ui.
      volumeHint: i18n.t('billing.plan.volumeHint'),
    };
  }
  return { price: i18n.t('billing.plan.perMonth', { amount: euro(o.priceMonthlyCents ?? 0) }), volumeHint: null };
}

// The per-request config the master reads AND the master writes need: system-admin-api
// does not auto-attach the tenant slug, so every call names the tenant itself. Exported
// because the writes now sit at their call sites and must not re-derive this (the same
// reason `dataProtection.workspaceExportConfig()` is exported).
//
// payment-api DOES auto-attach the slug, so its calls need no config at all.
export function masterCfg(params?: Record<string, string>) {
  return {
    headers: { 'X-Tenant-Slug': currentTenantSlug() },
    ...(params ? { params } : {}),
  } as never;
}

// ---- masterApi (plan catalog + lifecycle) --------------------------------

export async function fetchPlanCatalog(): Promise<PlanCatalog> {
  const { data } = await masterApi.get<PlanCatalog>('/plan/catalog', masterCfg());
  return data;
}
// Lightweight standing read for the banner CTA — avoids the full catalog fan-out.
export async function fetchStanding(): Promise<Standing> {
  const { data } = await masterApi.get<Standing>('/plan/standing', masterCfg());
  return data;
}
// Read-only: the exact amount a change/add would charge today, so the confirm
// modal names the prorated figure pre-click. Throws 503
// PRORATION_PREVIEW_UNAVAILABLE when the amount cannot be worked out — callers
// fall back to the generic note and never guess a figure.
// An add that books apprentice seats quotes WITH them: the amount the modal
// promises has to be the amount the POST takes for the number in that same modal.
// An add typed with a module code quotes WITH it (issue 1029, M2): the amount is the
// discounted one, and the answer carries the verdict on the code.
export async function previewProration(moduleKey: string, plan: string, apprenticeSeats?: number, promoCode?: string): Promise<ProrationPreview> {
  const params: Record<string, string> = { moduleKey, plan };
  if (apprenticeSeats != null) params.apprenticeSeats = String(apprenticeSeats);
  if (promoCode) params.promoCode = promoCode;
  const { data } = await masterApi.get<ProrationPreview>('/plan/proration-preview', masterCfg(params));
  return data;
}
// A requested seat booking (ADR 0056): at least one of the two quantities; an
// omitted metric keeps its current booking.
export interface SeatChangeTarget {
  userSeats?: number;
  apprenticeSeats?: number;
}
export interface SeatChangeResult {
  // 'scheduled' is the seat DECREASE on a paying account: the booking does not
  // move today, it moves at `effectiveAt` (issue 299 D9). A decrease while
  // trialing, and every increase, answer 'immediate'.
  applied: 'immediate' | 'scheduled' | 'noop';
  charged?: boolean;
  amountCents?: number;
  effectiveAt?: string;
  users: number;
  apprentices: number;
}
// Read-only: what a seat change takes from the card today. An INCREASE quotes the
// proration (0 for trial — billing starts at conversion on the then-current
// bookings); a DECREASE is never priced and answers 0 plus the `effectiveAt` the
// confirm must name (issue 299 D9). Same fallback contract as previewProration: on
// failure the confirm keeps the generic note and never guesses a figure. The
// decrease's live-usage floor runs here too, so the modal can never quote a
// reduction the POST would refuse.
export async function previewSeatChange(target: SeatChangeTarget): Promise<ProrationPreview> {
  const params: Record<string, string> = {};
  if (target.userSeats != null) params.userSeats = String(target.userSeats);
  if (target.apprenticeSeats != null) params.apprenticeSeats = String(target.apprenticeSeats);
  const { data } = await masterApi.get<ProrationPreview>('/plan/seats-preview', masterCfg(params));
  return data;
}
// ---- AI quota upgrades (#665) ---------------------------------------------
//
// Booked the way seats are booked (ADR 0056): the WHOLE booking after the change
// is sent, 0..n steps. Up = immediate + prorated (free while trialing); down = at
// the next billing date (immediate while trialing). The POST itself sits at its
// call site inside the mutation seam (AiQuotaPanel.vue), like every billing write.
export interface AiQuotaChangeResult {
  applied: 'immediate' | 'scheduled' | 'noop';
  charged?: boolean;
  amountCents?: number;
  effectiveAt?: string;
  scheduledChangeId?: string;
  steps: number;
}
// Read-only: what booking `steps` upgrades would take from the card today (an
// increase), or when a reduction lands (`scheduled` + `effectiveAt`). Same
// fallback contract as previewSeatChange: on failure the confirm keeps the
// generic note and never guesses a figure or a date.
export async function previewAiQuotaChange(steps: number): Promise<ProrationPreview> {
  const { data } = await masterApi.get<ProrationPreview>('/plan/ai-quota-preview', masterCfg({ steps: String(steps) }));
  return data;
}

// The plan codes every billing write shares, as the sentence to show. Read off
// the mutation seam's `outcome.code`; anything unlisted keeps the caller's own
// fallback, which talks about the change rather than about HTTP. Shared by the
// plan section and the AI quota card so one refusal reads the same in both.
export function planErrorMessage(code: string | undefined, t: (key: string) => string, fallback: string): string {
  if (code === 'PAYMENT_STANDING_REQUIRED') return t('billing.errors.paymentStanding');
  if (code === 'CARD_REQUIRED') return t('billing.errors.cardRequired');
  if (code === 'ENTERPRISE_CONTACT') return t('billing.plan.enterprise.body');
  // A workspace that has asked to cancel buys nothing (#299): the add door refuses
  // it rather than charging for a module its own cancellation would switch off
  // days later, and the quote door refuses the same way. The copy points at the
  // one action that unblocks it — "Keep subscription", by the name the danger zone
  // gives it — because "could not add the module" would leave the owner nowhere.
  if (code === 'CANCELLATION_PENDING') return t('billing.errors.cancellationPending');
  return fallback;
}

// The open scheduled upgrade DECREASE, if any — matched on its metric, never on
// position or on `changeType` alone (a seat decrease is the same changeType).
export function pendingAiQuotaChange(changes: ScheduledChange[] | null | undefined): ScheduledChange | undefined {
  return (changes ?? []).find(
    (c) => c.changeType === 'seat_change' && c.seatMetric === AI_QUOTA_STEPS_METRIC && c.targetSeats != null,
  );
}

// May the upgrade be changed here at all? Only while a quota is in force: a
// workspace on its own provider key has none to raise (the server refuses with
// AI_QUOTA_OWN_KEY), a suspended one has none to spend, and an operator's
// explicit unlimited makes a step buy nothing.
export function aiQuotaUpgradable(quota: AiQuota | null | undefined): boolean {
  return !!quota && (quota.source === 'derived' || quota.source === 'override');
}

// Percent of the quota spent this period, clamped to 0..100; null when there is
// no ceiling to measure against (0% would read as "using nothing").
export function aiQuotaUsedPercent(quota: Pick<AiQuota, 'limit' | 'used'> | null | undefined): number | null {
  if (!quota || quota.limit == null) return null;
  if (quota.limit <= 0) return 100;
  return Math.min(100, Math.max(0, Math.round((quota.used / quota.limit) * 100)));
}

// The monthly amount of the billed lines that ride on NO seat metric — today the
// AI quota upgrade line alone. The seat calculator adds it to its seat arithmetic,
// because the server's billed total (`seatPricing.monthlyTotalCents`) carries it:
// without it the calculator could never reproduce the bill for a workspace with
// an upgrade booked, and would hide itself. Null when such a line has no amount —
// a total that quietly omits a line would be the wrong answer.
export function nonSeatLinesCents(lines: PlanLine[] | null | undefined): number | null {
  let sum = 0;
  for (const line of lines ?? []) {
    if (line.moduleKey !== AI_QUOTA_MODULE_KEY) continue;
    if (typeof line.amountCents !== 'number') return null;
    sum += line.amountCents;
  }
  return sum;
}

// How an AI quota upgrade line reads wherever a billed line is listed (the
// booking table, the seat calculator): its invoice name, the price of one step,
// and the quantity — `quantity × unit`, the way the invoice prints it. One helper
// so the two lists cannot word the same line differently.
export function describeAiQuotaLine(
  line: PlanLine,
  i18n: { t: (key: string, named?: Record<string, unknown>) => string; locale: string },
): { name: string; price: string | null; count: string | null } {
  const euro = (cents: number) =>
    new Intl.NumberFormat(i18n.locale, { style: 'currency', currency: 'EUR' }).format((cents ?? 0) / 100);
  return {
    name: i18n.t('billing.planNames.ai_quota.upgrade'),
    price: line.pricePerSeatCents != null ? i18n.t('billing.plan.perUpgradeMonth', { amount: euro(line.pricePerSeatCents) }) : null,
    count: line.seatCount != null ? i18n.t('billing.calc.aiQuotaUpgradesCount', { count: line.seatCount }) : null,
  };
}

export async function fetchLifecycle(): Promise<LifecycleState> {
  const { data } = await masterApi.get<LifecycleState>('/tenant/lifecycle', masterCfg());
  return data;
}

// ---- userApi (tenant billing-status) -------------------------------------

// The banner contract user-api serves at /tenant/billing-status. billingExempt is
// a MASTER Tenant field, and user-api (master access) surfaces it here alongside
// the stage/deadline/roles the banner already reads — so the tenant billing view
// can confirm the exemption in-product without a payment-api → master hop (issue
// 213 F21). userApi does not auto-attach the slug, so the tenant is named per call
// (same per-request header pattern as the master reads above; masterCfg is just
// { X-Tenant-Slug }).
export interface BillingStatus {
  stage: string | null;
  deadline: string | null;
  isOwner: boolean;
  isBillingManager: boolean;
  isSuiteAdmin: boolean;
  billingExempt: boolean;
  // Is the caller the person who signed this workspace up (master
  // Tenant.createdByUserId)? The account-less billing card needs it to name the
  // right recovery for a stranded signup (issue 364 F25): only that buyer's
  // return to checkout resumes THIS workspace. Not `isOwner` — ownership
  // transfers (#211), the signup does not.
  isSignupBuyer: boolean;
}
export async function fetchBillingStatus(): Promise<BillingStatus> {
  const { data } = await userApi.get<BillingStatus>('/tenant/billing-status', masterCfg());
  return data;
}

// ---- paymentApi (profile completion + card + handoff) --------------------
//
// Only the shape survives here. The three writes it describes (complete the billing
// profile, remove the card, mint a handoff link) live at their forms and buttons —
// `BillingProfileForm.vue` and `views/BillingView.vue` — inside the seam.

export interface CompleteProfileInput {
  legalName: string;
  addressLine1: string;
  addressLine2?: string;
  postalCode: string;
  city: string;
  vatId?: string;
}

// Mollie's card expiry ("YYYY-MM") as a localized month/year, e.g. "02/2028".
// Anything else is shown as received rather than guessed at.
export function formatCardExpiry(raw: string, locale: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(raw);
  if (!m) return raw;
  return new Intl.DateTimeFormat(locale, { month: '2-digit', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)));
}
